import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useCreateDocumentsMutation, useReplaceDocumentMutation } from 'api/main';
import { docSubtypes, docTypes, ID_DOC_SUBTYPES } from 'utils/constants';
import { validateFileSize, validateFileType } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import {
    ActionButton,
    CancelButton,
    CustomDialogTitle,
    CustomSelect,
    FileErrorMessage,
    FileInputButton,
} from 'components';
import { Box, Dialog, DialogActions, DialogContent, MenuItem, Typography } from '@mui/material';

const idDocSubtypeLabels = {
    [docSubtypes.ID_CARD]: texts.ID_CARD,
    [docSubtypes.PASSPORT]: texts.PASSPORT,
    [docSubtypes.DRIVING_LICENSE]: texts.DRIVING_LICENSE,
    [docSubtypes.CREDIT_CARD]: texts.CREDIT_CARD,
};

const IdDocumentDialog = props => {
    const { id, open, handleClose, title = texts.REPLACE, userId, subtype } = props;

    const [form, setForm] = useState({
        selectedFile: null,
        fileType: subtype || docSubtypes.ID_CARD,
    });
    const [typeError, setTypeError] = useState('');
    const [fileError, setFileError] = useState('');
    const [
        replaceDocument,
        { isLoading: replaceLoading, isSuccess: replaceSuccess, reset: resetReplace },
    ] = useReplaceDocumentMutation();
    const [
        createDocuments,
        { isLoading: createLoading, isSuccess: createSuccess, reset: resetCreate },
    ] = useCreateDocumentsMutation();

    const handleFileChange = useCallback(
        e => {
            const file = e.target.files[0];

            if (validateFileType(file, docTypes.ID, subtype)) {
                if (validateFileSize(file)) {
                    setForm(prevState => ({ ...prevState, selectedFile: file }));
                    setFileError('');
                } else {
                    setFileError('size');
                }
            } else {
                setFileError('type');
            }
        },
        [subtype]
    );

    const handleTypeChange = useCallback(e => {
        setForm(prevState => ({ ...prevState, fileType: e.target.value }));
    }, []);

    const handleSubmit = useCallback(() => {
        if (!form.fileType) {
            setTypeError(texts.VEUILLEZ_CHOISIR_UNE_OPTION);
        }
        if (!form.selectedFile) {
            setFileError('empty');
        }
        if (form.selectedFile && form.fileType) {
            const formData = new FormData();
            formData.append('type', docTypes.ID);
            formData.append('subtype', form.fileType);
            if (id) {
                formData.append('file', form.selectedFile);
                replaceDocument({ id, formData });
            } else {
                formData.append('files[]', form.selectedFile);
                formData.append('belongsToUserId', userId);
                createDocuments({ formData });
            }
        }
    }, [form.fileType, form.selectedFile, id, replaceDocument, userId, createDocuments]);

    useEffect(() => {
        if (replaceSuccess || createSuccess) {
            handleClose();
        }
    }, [replaceSuccess, handleClose, createSuccess]);

    useEffect(() => {
        if (!open) {
            resetReplace();
            resetCreate();
            setForm({
                selectedFile: null,
                fileType: docSubtypes.ID_CARD,
            });
        }
    }, [open, resetReplace, resetCreate]);

    return (
        <Dialog open={open}>
            <CustomDialogTitle title={title} handleClose={handleClose} />
            <DialogContent>
                <CustomSelect
                    label={texts.DOCUMENT_TYPE}
                    value={form.fileType}
                    onChange={handleTypeChange}
                    error={!!typeError}
                    helperText={typeError}
                >
                    {ID_DOC_SUBTYPES.map(opt => (
                        <MenuItem key={opt} value={opt}>
                            {idDocSubtypeLabels[opt]}
                        </MenuItem>
                    ))}
                </CustomSelect>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexDirection="column"
                >
                    {form.selectedFile && <Typography>{form.selectedFile.name}</Typography>}
                    <FileInputButton
                        id="id-replacement"
                        onFileChange={handleFileChange}
                        label={texts.UPLOAD_FILE_LABEL}
                    />
                    {!!fileError && <FileErrorMessage variant={fileError} useMargin={false} />}
                </Box>
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={replaceLoading || createLoading} onClick={handleClose}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton
                    disabled={replaceLoading || createLoading}
                    onClick={handleSubmit}
                    loading={replaceLoading || createLoading}
                >
                    {texts.SAVE}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

IdDocumentDialog.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    title: PropTypes.string,
    subtype: PropTypes.string,
    userId: PropTypes.number,
};

export default IdDocumentDialog;
